import {
    CategorySectionProps,
    EventPlatformEntry,
    FilterProps,
    FilterValues,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import { FilterMenuProps, NoResults } from "@kaltura/mediaspace-shared-search";
import { MediaGrid } from "@kaltura/mediaspace-shared-media-grid";
import MediaList from "./list/MediaList";
import { MediaSearchBar } from "./MediaSearchBar";
import { Suspense, useCallback, useEffect, useState } from "react";
import { DataLoader } from "../DataLoader";
import { baseUrl, fetchKmsData } from "@kaltura/mediaspace-shared-utils";
import { MediaContainerSkeleton } from "./skeleton/MediaContainerSkeleton";

export interface MediaContainerProps extends CategorySectionProps<EventPlatformEntry[]> {
    isGridView: boolean;
    isVerticalView: boolean;
    showSpeakersForContentSectionEntries?: boolean;
    /** filers definitions */
    filters: FilterProps<SupportedKmsTypeFilter>[];
    /** props passed down to the search bar's filter menu */
    filterMenuProps: FilterMenuProps;
    showSearchInput: boolean;
}

/**
 * The `MediaContainer` component renders media content in either a grid or list view.
 * It also includes a search bar for filtering media content.
 */
export const MediaContainer = ({
    isGridView,
    filters,
    filterMenuProps,
    content,
    categoryId,
    isVerticalView,
    showSpeakersForContentSectionEntries = false,
    showSearchInput,
    ...rest
}: MediaContainerProps) => {
    // State to manage the content to be displayed
    const [contentToShow, setContentToShow] = useState<EventPlatformEntry[]>(content);
    // State to manage whether a search is active
    const [isSearch, setIsSearch] = useState(false);
    // State to manage the search results fetcher function
    const [searchResultsFetcher, setSearchResultsFetcher] = useState<() => EventPlatformEntry[]>();

    // Update the content to show when the content prop changes
    // (content might be changed by the parent component when searching in vertical view)
    useEffect(() => {
        setContentToShow(content);
    }, [content]);

    /** Function to render the content based on the view mode. */
    const renderContent = () => {
        // if no media results to show in view do not render anything
        if (contentToShow.length === 0) {
            return null;
        }

        if (isGridView) {
            return (
                <MediaGrid
                    content={contentToShow}
                    categoryId={categoryId}
                    showPagination={!isVerticalView}
                    {...rest}
                    // `rest` might include `analyticsValue` prop - override it
                    analyticsValue={"Media thumbnail link"}
                />
            );
        }
        else {
            return (
                <MediaList
                    content={contentToShow}
                    categoryId={categoryId}
                    headingLevel={isVerticalView ? "h3" : "h2"}
                    additionalParams={{
                        showSpeakersForContentTabEntries: showSpeakersForContentSectionEntries,
                        analyticsValue: "Media",
                    }}
                    {...rest}
                />
            );
        }
    };

    /** Callback function to handle filter changes. */
    const onFilterChange = useCallback(
        (filterValues: FilterValues) => {
            setContentToShow([]);
            const url = `${baseUrl}/channelthemingui/index/search-media/channelId/${categoryId}`;
            setSearchResultsFetcher(() => fetchKmsData<EventPlatformEntry[]>(url, filterValues, true));
            setIsSearch(true);
        },
        []
    );

    /** Callback function to handle clearing of filters. */
    const onFiltersCleared = useCallback(() => {
        setIsSearch(false);
        setContentToShow(content);
    }, [content]);

    /** Callback function to handle the loading of search results. */
    const onSearchResultsLoaded = (searchResults: EventPlatformEntry[]) => {
        setContentToShow(searchResults);
    };

    return (
        <div>
            {!isVerticalView && (
                <MediaSearchBar
                    filters={filters}
                    filterMenuProps={filterMenuProps}
                    onFilterChange={onFilterChange}
                    onFiltersCleared={onFiltersCleared}
                    showSearchInput={showSearchInput}
                />
            )}

            {isSearch && searchResultsFetcher ? (
                <Suspense fallback={<MediaContainerSkeleton isGridView={isGridView} />}>
                    <DataLoader fetchKmsData={searchResultsFetcher} onLoaded={onSearchResultsLoaded} />
                    {contentToShow.length === 0 && <NoResults screenMode={"nomatches"} />}
                    {renderContent()}
                </Suspense>
            ) : (
                renderContent()
            )}
        </div>
    );
};
